import { lazy, useEffect, useEffectEvent } from 'react';

import { sendNativeMessage } from 'hooks/useNativeListener';
import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { Toaster } from 'react-hot-toast';

import { usePlanPageTimeTracker } from '@/hooks/usePlanPageTimeTracker';
import { useSearchSessionTracker } from '@/hooks/useSearchSessionTracker';

import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import { PrintReport } from '@/components/print-report';

import Constants from '@/constants';

import MainContent from './Content/MainContent';
import Footer from './Footer/Footer';
import LayoutWrapper from './LayoutWrapper/LayoutWrapper';
import Navbar from './Navbar/Navbar';
import SmartBannerContainer from './SmartBanner/SmartBannerContainer';
import TrainingModeWrapper from './TrainingModeWrapper/TrainingModeWrapper';
import WebAlertBannerContainer from './WebAlertBanner/WebAlertBannerContainer';

import { Routes } from '@/app';
import { trainingModeAtom } from '@/atoms/trainingMode';
import { userPrefsAtom } from '@/atoms/userPrefs';
import { env } from '@/env';

const CommandMenu = lazy(
  () => import('@/components/developer-menu/CommandMenu'),
);

const setIsBeforeNavigateAtom = focusAtom(trainingModeAtom, o =>
  o.prop('isBeforeNavigate'),
);

const isTrainingModeAtom = focusAtom(trainingModeAtom, o =>
  o.prop('isTrainingMode'),
);

const Layout = () => {
  const setIsBeforeNavigate = useSetAtom(setIsBeforeNavigateAtom);
  const isTrainingMode = useAtomValue(isTrainingModeAtom);

  useSearchSessionTracker();
  usePlanPageTimeTracker();

  const routeMatches = useRouteMatcher();
  const isSignalHeadlessView = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.SignalPath,
  );

  const userLanguage = useAtomValue(userPrefsAtom).language;

  const sendLanguageChangedEvent = useEffectEvent(() => {
    sendNativeMessage({
      type: 'languageChanged',
      payload: userLanguage,
    });
  });

  useEffect(() => {
    sendLanguageChangedEvent();
    // https://github.com/kutlugsahin/react-smooth-dnd/issues/75
    // This library hasn't been updated since 2020.
    const cleanClasses = () => {
      document.body.classList.remove(
        'smooth-dnd-no-user-select',
        'smooth-dnd-disable-touch-action',
      );
    };
    document.addEventListener('touchend', cleanClasses, false);
    return () => {
      document.removeEventListener('touchend', cleanClasses, false);
    };
  }, []);

  useEffect(() => {
    setIsBeforeNavigate(false);
  }, [setIsBeforeNavigate]);

  return !isSignalHeadlessView ? (
    <LayoutWrapper>
      <TrainingModeWrapper>
        <WebAlertBannerContainer />
        <Navbar className={cn(isTrainingMode && 'hidden!')} />
        <div
          className={cn(
            `p-0 md:min-h-screen!`,
            isTrainingMode
              ? 'ml-0! lg:ml-0!'
              : 'md:ml-14! lg:ml-(--sidenav-width)!',
          )}
        >
          {env.REACT_APP_ENV !== 'prod' && <CommandMenu />}
          <SmartBannerContainer />
          <MainContent>
            <Toaster position="top-center" />
            <div style={{ marginTop: 'var(--web-alert-banner-height, 0px)' }}>
              <Routes />
            </div>
          </MainContent>
          <Footer />
        </div>
        <PrintReport />
      </TrainingModeWrapper>
    </LayoutWrapper>
  ) : (
    <MainContent>
      <Toaster position="top-center" />
      <Routes />
    </MainContent>
  );
};

export default Layout;
